<template>
  <div v-if="plans">
    <v-card-text>
      <v-form ref="form" v-model="valid">
        <v-row>
          <v-col cols="12" md="3" lg="2">
            <v-autocomplete
              name="plan"
              :menu-props="{ contentClass: 'text-left benefits' }"
              dense
              v-model="searchForm.planIdentity"
              :items="filteredPlans"
              :item-text="planDescription"
              item-value="Id"
              clearable
              outlined
              :label="$t('globals.plan')"
              return-object
              @keyup="keyUp"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" md="3" lg="2">
            <no-autocomplete-textfield
              name="certificate"
              dense
              outlined
              clearable
              v-model="searchForm.certificate"
              :label="$t('headers.members_management.Certificate')"
              @keyup="keyUp"
            />
          </v-col>

          <v-col cols="12" md="3" lg="2">
            <no-autocomplete-textfield
              name="first_name"
              dense
              outlined
              clearable
              v-model="searchForm.firstName"
              :label="$t('headers.members_management.First Name')"
              @keyup="keyUp"
            />
          </v-col>

          <v-col cols="12" md="3" lg="2">
            <no-autocomplete-textfield
              name="last_name"
              dense
              outlined
              clearable
              v-model="searchForm.lastName"
              :label="$t('headers.members_management.Last Name')"
              @keyup="keyUp"
            />
          </v-col>

          <v-col cols="12" md="2" lg="1">
            <v-autocomplete
              v-model="searchForm.operator"
              :items="operators"
              outlined
              dense
              :label="$t('components.searchmemberformhours.operator')"
              @keyup="keyUp"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn
        text
        color="primary"
        @click="submitSearchForm"
        :disabled="
          searchForm.planIdentity === null &&
            searchForm.firstName === null &&
            searchForm.lastName === null &&
            searchForm.certificate === null
        "
      >
        {{ $t("pbascomponents.pbasdatatable.search") }}
      </v-btn>
      <v-btn
        text
        @click="
          searchForm.operator = operators[0];
          searchForm.planIdentity = null;
          searchForm.firstName = null;
          searchForm.lastName = null;
          submitSearchForm();
        "
      >
        {{ $t("globals.reset") }}
      </v-btn>
    </v-card-actions>
    <template v-if="showTable">
      <pbas-data-table
        ref="table"
        :dataProvider="provider"
        :rowActions="rowActions"
        fileNamePrefix="members"
        identifier="search-member-table"
        :noSearch="true"
        :noActions="true"
        :exportDataSwitch="true"
        @error="error = $event"
        :exporters="$exporters"
        :showSelect="false"
      ></pbas-data-table>
    </template>
    <v-dialog scrollable v-model="loading" persistent max-width="200">
      <v-card>
        <v-card-title>{{ $t("globals.please_wait") }}</v-card-title>
        <v-card-text>
          <v-container fluid grid-list-md>
            <v-layout row justify-center>
              <v-flex xs12 sm6 md4 lg3>
                <v-progress-circular indeterminate></v-progress-circular>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
    <error-handler :error="error"></error-handler>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ErrorHandler from "@/components/ErrorHandler";
import { ODataProvider as PbasOData } from "pbas-vue-components";
import MemberHeaders from "@/headers/SearchMembersClaims";
import NoAutocompleteTextfield from "@/components/NoAutocompleteTextfield";
import { translate2 } from "@/trans2";

export default {
  name: "search-members-form",
  components: {
    ErrorHandler,
    NoAutocompleteTextfield,
  },
  props:["coverageType"],
  data() {
    return {
      showTable: false,
      panel: [0, 1],
      searchForm: {
        planIdentity: null,
        firstName: null,
        lastName: null,
        certificate: null,
      },
      error: null,
      provider: undefined,
      rowActions: [
        {
          name: "viewItem",
          icon: "check",
          title: "globals.select",
          handler: (item) => {
            this.loadMemberDetails(item);
          },
          visible: (item) => {
            return item;
          },
        },
      ],
      valid: false,
      operators: ["And", "Or"],
      loading: false,
      memberIdentity: null,
    };
  },
  computed: {
    ...mapGetters("common", ["language", "settings"]),
    ...mapGetters("auth", ["isLoggedIn", "user"]),
    ...mapGetters("plansMembers", { columnsDropDownOptions: "options" }),
    ...mapGetters("plansMembers", { planParameters: "planOptions" }),
    ...mapGetters("common", ["plans", "claimsPlan"]),
    filteredPlans() {
      if (this.coverageType === "HSA") {
        return this.plans.filter((o) => o.HasHSA && o.PlanType === 'HW');
      }
      return this.plans.filter((o) => o.PlanType === 'HW');
    }
  },
  mounted() {
    const url = "/find-members";
    this.provider = new PbasOData(this.$axiosHttp, url, MemberHeaders);
    this.searchForm.operator = this.operators[0];
    this.loadUserPlans();
  },
  methods: {
    ...mapActions("plansMembers", ["getMemberInfo", "getUserPlans"]),
    ...mapActions("auth", ["silentRefresh", "login"]),
    ...mapActions("common", ["setPlans", "setClaimsPlan"]),
    planDescription(plan) {
      const desc = translate2(plan, "Name");
      if (desc.indexOf(plan.UniqueIdentifier) === 0) {
        return desc;
      }
      return plan.UniqueIdentifier + "-" + desc;
    },
    async loadUserPlans() {
      try {
        var usr = await this.silentRefresh();
        if (usr) {
          let plans = (await this.getUserPlans()).data.value;
          this.setPlans(
            plans.map((o) => {
              o.Name = o.Current.Name;
              o.Description = o.Current.Description;
              o.Translations = o.Current.Translations;
              return o;
            })
          );
          if (this.claimsPlan !== null && this.claimsPlan !== null) {
            this.searchForm.planIdentity = this.claimsPlan;
          }
        }
      } catch (error) {
        //console.log("selectPlan: silent refresh error: ", error);
        this.login();
      }
    },
    getPlanName(item) {
      return translate2(item, "Name");
    },
    getPlanNumber(item) {
      return item && item.UniqueIdentifier;
    },
    getPlanStartDate(item) {
      // console.log(item);
      return item && item.FirstRenewalDate;
    },
    getDataAsync() {
      return this.$refs.table.getDataAsync();
    },
    async submitSearchForm() {
      const filters = [];
      this.provider.filter = undefined;
      if (this.searchForm.planIdentity) {
        filters.push(`PlanIdentityId eq ${this.searchForm.planIdentity.Id}`);
        this.setClaimsPlan(this.searchForm.planIdentity);
      }
      if (this.searchForm.firstName) {
        filters.push(
          `contains(tolower(CurrentMember/FirstName), '${this.searchForm.firstName
            .toLowerCase()
            .replaceAll("'", "''")}')`
        );
      }
      if (this.searchForm.lastName) {
        filters.push(
          `contains(tolower(CurrentMember/LastName), '${this.searchForm.lastName
            .toLowerCase()
            .replaceAll("'", "''")}')`
        );
      }
      if (this.searchForm.certificate) {
        filters.push(
          `contains(tolower(MemberIdentification/Certificate), '${this.searchForm.certificate
            .toLowerCase()
            .replaceAll("'", "''")}')`
        );
      }
      if (this.coverageType === "HSA") {
        filters.push(
          "(CurrentEmploymentInformation/Employer/Current/HasHealthSpendingAccount eq null or CurrentEmploymentInformation/Employer/Current/HasHealthSpendingAccount eq true)"
        );
      }

      if (filters.length > 0) {
        let f = filters.join(` ${this.searchForm.operator.toLowerCase()} `);
        // console.log(filters, f);
        this.provider.filter = f;
        this.showTable = true;
        this.$nextTick(() => {
          this.$refs.table.getDataAsync();
        });
      } else {
        this.showTable = false;
      }
    },

    async loadMemberDetails(memberIdentity) {
      const planIdentity = this.plans.filter(
        (o) => o.Id === memberIdentity.PlanIdentityId
      )[0];
// console.log(planIdentity)
      this.$emit("plan-name", this.getPlanName(planIdentity));
      this.$emit("plan-number", this.getPlanNumber(planIdentity));
      this.$emit("plan-start-date", this.getPlanStartDate(planIdentity));

      this.loading = true;
      try {
        //CurrentMemberBenefits($expand=CoverageIdentity($expand=Current($expand=Translations($select=LanguageCode,Name)))),
        this.memberIdentity = (await this.getMemberInfo({
          planIdentityId: memberIdentity.PlanIdentityId,
          memberIdentityId: memberIdentity.Id,
          odataQuery:
            "$select=Id,Email,PlanIdentityId,PlanEntryDate,TerminatedAt&$expand=CurrentMembershipStatus($expand=MemberGroupIdentity($select=Id)),MemberIdentification($select=Certificate),CurrentBankAccount,CurrentMember($select=LastName,FirstName,MiddleName,DateOfBirth),CurrentDependants($select=Id,IdentityId,FirstName,MiddleName,LastName,DateOfBirth;$expand=Relationship($expand=Translations),Identity($select=Code,IsEligible)),CurrentContactInformation($expand=Address($expand=Country($expand=Translations),Province($expand=Translations)))",
        })).data;

        this.$emit("select", this.memberIdentity);
      } catch (error) {
        this.error = error;
      } finally {
        this.loading = false;
      }
    },

    keyUp(evt) {
      if (evt.keyCode === 13) {
        // enter
        this.submitSearchForm();
        return;
      }
      return;
    },
  },
};
</script>
